import React from "react";
import styled from "styled-components";
import { CurrencyEnumType } from "@ifgengineering/client-invest-sdk";
import { V3ProgressBar, V3Text } from "@components/v3/primitives";
import { DealFundraisingCapacity, isGaugeVisible } from "@state/dealCapacity";
import { formatCurrencyWhole } from "../format";

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm}px;
  padding-top: ${({ theme }) => theme.space.card.inset}px;
  border-top: ${({ theme }) => theme.stroke.md}px solid
    ${({ theme }) => theme.colors.ui.border.divider};
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space.md}px;
`;

interface FundraisingGaugeProps {
  capacity: DealFundraisingCapacity;
  currency: CurrencyEnumType;
  testID: string;
}

/** Subscribed progress for a capped deal, or the notice once its round closed. */
export const FundraisingGauge = ({
  capacity,
  currency,
  testID,
}: FundraisingGaugeProps): JSX.Element | null => {
  if (capacity.windowStart == null) {
    return (
      <Block data-testid={testID}>
        <V3Text variant="label.sm" color="secondary">
          Fundraising round closed
        </V3Text>
        <V3Text variant="body.sm" color="tertiary">
          No longer accepting new investment
        </V3Text>
      </Block>
    );
  }

  if (capacity.fundraisingCap == null) return null;

  const cap = capacity.fundraisingCap;
  const remaining = Math.max(capacity.remainingCapacity ?? 0, 0);
  const raised = Math.max(cap - remaining, 0);
  const percentage = cap > 0 ? Math.round((raised / cap) * 100) : 0;

  return (
    <Block data-testid={testID}>
      <V3Text variant="label.sm" color="secondary">
        {`Fundraising Limit — ${formatCurrencyWhole(cap, currency)}`}
      </V3Text>
      {isGaugeVisible(capacity) ? (
        <>
          <V3ProgressBar
            value={raised}
            max={cap}
            tone="positive"
            label={`${percentage}% subscribed`}
            testID={`${testID}-progress`}
          />
          <MetaRow>
            <V3Text variant="body.sm" color="tertiary">
              {`${formatCurrencyWhole(remaining, currency)} left`}
            </V3Text>
            <V3Text variant="caption.sm-medium" color="tertiary">
              {`${percentage}%`}
            </V3Text>
          </MetaRow>
        </>
      ) : null}
    </Block>
  );
};
